import { inject, Injectable } from '@angular/core'
import * as Sentry from '@sentry/angular'
import { filter, first } from 'rxjs'
import { UsersService } from './users.service'
import { LoggerService } from './logger.service'
import { MeUser } from './users.service'
import { environment } from '../environments/environment'

const STORAGE_HOST = 'storage.googleapis.com'

// A client with useVirtualHostedStorageUrls gets its signed URLs as <bucket>.storage.googleapis.com,
// which have to be redacted just the same.
function isVirtualHostedStorageHost(hostname: string) {
  return hostname.endsWith(`.${STORAGE_HOST}`)
}

function sameHost(url: string) {
  try {
    const hostname = new URL(url).hostname
    return hostname === STORAGE_HOST || isVirtualHostedStorageHost(hostname)
  } catch {
    return false
  }
}

// A meeting name searched for or filtered on travels in the page and request URLs.
const SENSITIVE_QUERY_PARAMS = ['secret', 'nameQuery', 'filter.name', 'filter.name.contains']

function stripSensitiveQueryString(queryString: string): string {
  const params = new URLSearchParams(queryString)
  const sensitive = SENSITIVE_QUERY_PARAMS.filter((param) => params.has(param))
  if (sensitive.length === 0) {
    return queryString
  }
  sensitive.forEach((param) => params.set(param, '[Filtered]'))
  return params.toString()
}

function stripSensitiveQueryParams(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin)
    let changed = false
    for (const param of SENSITIVE_QUERY_PARAMS) {
      if (parsed.searchParams.has(param)) {
        parsed.searchParams.set(param, '[Filtered]')
        changed = true
      }
    }
    return changed ? parsed.toString() : url
  } catch {
    return url
  }
}

function redactUrl(url: string) {
  try {
    const parsed = new URL(url)
    const parts = parsed.pathname.split('/').filter(Boolean)
    // Path-style URLs lead with the bucket; the object key starts after it.
    const keptParts = parts.slice(0, isVirtualHostedStorageHost(parsed.hostname) ? 1 : 2)
    const fileName = parts[keptParts.length] ?? null
    const maskedFileName = fileName === null ? [] : [`*****${fileName.lastIndexOf('.') >= 0 ? fileName.slice(fileName.lastIndexOf('.')) : ''}`]
    return parsed.origin + '/' + [...keptParts, ...maskedFileName].join('/')
  } catch {
    return url
  }
}

const SENSITIVE_BODY_FIELDS = ['link']

// Responses that list meeting names, which masking the DOM alone would still let through the network tab.
const SENSITIVE_BODY_FIELDS_BY_PATH: Record<string, string[]> = {
  '/api/meetings/client': ['name'],
}

function sensitiveBodyFieldsFor(url: string) {
  try {
    return [...SENSITIVE_BODY_FIELDS, ...(SENSITIVE_BODY_FIELDS_BY_PATH[new URL(url, window.location.origin).pathname] ?? [])]
  } catch {
    return SENSITIVE_BODY_FIELDS
  }
}

function redactBodyFields(body: unknown, fields: string[]): unknown {
  if (typeof body === 'string') {
    return fields.reduce((redacted, field) => redacted.replace(new RegExp(`"${field}"\\s*:\\s*"(?:[^"\\\\]|\\\\.)*"`, 'g'), `"${field}":"[Filtered]"`), body)
  }
  if (Array.isArray(body)) {
    return body.map((item) => redactBodyFields(item, fields))
  }
  if (typeof body === 'object' && body !== null) {
    return Object.fromEntries(Object.entries(body).map(([key, value]) => [key, fields.includes(key) ? '[Filtered]' : redactBodyFields(value, fields)]))
  }
  return body
}

function redactUrlsInText(text: string) {
  if (typeof text !== 'string') {
    return text
  }
  return text.replace(/https?:\/\/[^\s"')]+/g, (match) => (sameHost(match) ? redactUrl(match) : match))
}

@Injectable({
  providedIn: 'root',
})
export class SentryService {
  usersService = inject(UsersService)
  loggerService = inject(LoggerService)

  constructor() {
    this.usersService.currentUser$
      .pipe(
        filter((user): user is MeUser => user !== null),
        first()
      )
      .subscribe((user) => this.initializeSentry(user))
  }

  captureMessage(message: string, context?: Parameters<typeof Sentry.captureMessage>[1]) {
    Sentry.captureMessage(message, context)
  }

  captureException(error: unknown, context?: Parameters<typeof Sentry.captureException>[1]) {
    Sentry.captureException(error, context)
  }

  private initializeSentry(user: MeUser) {
    if (environment.disableSentry) {
      return
    }
    Sentry.init({
      dsn: 'https://7f307e453ac734768ca5bda335e6880b@sentry.wiseworks.ai/2',
      release: 'wiseworks-web@1.0.0',
      environment: user.environment,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          mutationLimit: 30000,
          maskAllText: false,
          maskAllInputs: false,
          blockAllMedia: false,
          mask: [
            'app-outline',
            'app-tiptap-editor',
            'app-transcript',
            '.search-container .right .match .content',
            'app-previous-meeting-card .thumbnail',
            'app-template-item .item-title',
            'app-template-item .item-title-input',
            'app-ai-action-streaming-dialog',
            'app-ai-action-fact-check-dialog',
            'app-meeting app-toolbar .title',
            'app-meeting-setup-page app-toolbar .title',
            'app-previous-meeting-card .title',
            'app-upcoming-meeting-card .title',
            'app-ongoing-meeting-card .name',
            'app-speaker-x-ray',
            '.search-container .right .match .speaker',
            'app-meeting-setup .attendee-name',
            '.speaker-menu-content',
            '.mask-attendee-names',
            '.mask-file-names',
            '.mask-meeting-link',
            '.mask-meeting-name',
            // The meeting page names the browser tab after the meeting.
            'title',
            // CDK copies every tooltip's text into this visually hidden container for screen readers, so a
            // masked tooltip would otherwise still land in the replay DOM through it.
            '.cdk-describedby-message-container',
            'input[type=file]',
            'app-formatted-text-editor ngx-editor',
            'app-template-item-content-block textarea',
            'app-action-items',
            'app-action-items input',
            'app-action-items textarea',
            // The assignee and due-date menus render in the CDK overlay, outside app-action-items.
            '.action-item-menu',
            '.mask-transcript-passage',
          ],
          unmask: ['mat-icon', '.material-icons', '.material-icons-outlined', '.material-symbols-outlined'],
          block: ['video', 'app-meeting video', '.video-player', '.intercom-app'],
          networkDetailAllowUrls: [window.location.origin],
          networkCaptureBodies: true,
          networkRequestHeaders: ['User-Agent'],
          networkResponseHeaders: ['Content-Type'],
          beforeAddRecordingEvent(event: any) {
            const payload = event && event.data && event.data.payload
            if (payload === null || payload === undefined) {
              return event
            }

            if (event.data.tag === 'performanceSpan') {
              const bodyFields = typeof payload.description === 'string' ? sensitiveBodyFieldsFor(payload.description) : SENSITIVE_BODY_FIELDS
              if (payload.description) {
                payload.description = redactUrlsInText(stripSensitiveQueryParams(payload.description))
              }
              for (const side of ['request', 'response']) {
                const captured = payload.data && payload.data[side]
                if (captured && captured.body !== null && captured.body !== undefined) {
                  captured.body = redactBodyFields(captured.body, bodyFields)
                }
              }
              if (payload.data && payload.data.url && sameHost(payload.data.url)) {
                payload.data.url = redactUrl(payload.data.url)
              }
              if (payload.name) {
                payload.name = redactUrlsInText(payload.name)
              }
            }

            if (event.data.tag === 'breadcrumb') {
              if (payload && payload.message) {
                payload.message = redactUrlsInText(payload.message)
              }
              if (payload && payload.data && payload.data.url && sameHost(payload.data.url)) {
                payload.data.url = redactUrl(payload.data.url)
              }
            }

            return event
          },
        }),
      ],
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 1.0,
      replaysOnErrorSampleRate: 1.0,
      // Request and response bodies carry meeting content, which only the replay's own redaction above may let through.
      dataCollection: {
        httpBodies: [],
      },
      // Keeps captureMessage events grouped as before and out of the errored-session count.
      attachStacktrace: false,
    })

    Sentry.addEventProcessor((event: any) => {
      if (event && event.type === 'replay_event' && Array.isArray(event.urls)) {
        event.urls = event.urls.map((url: string) => {
          const stripped = stripSensitiveQueryParams(url)
          return sameHost(stripped) ? redactUrl(stripped) : stripped
        })
      }
      if (event.request?.url) {
        event.request.url = stripSensitiveQueryParams(event.request.url)
      }
      if (typeof event.request?.query_string === 'string') {
        event.request.query_string = stripSensitiveQueryString(event.request.query_string)
      }
      if (Array.isArray(event.breadcrumbs)) {
        for (const breadcrumb of event.breadcrumbs) {
          if (breadcrumb.data?.url) {
            breadcrumb.data.url = stripSensitiveQueryParams(breadcrumb.data.url)
          }
          if (breadcrumb.data?.from) {
            breadcrumb.data.from = stripSensitiveQueryParams(breadcrumb.data.from)
          }
          if (breadcrumb.data?.to) {
            breadcrumb.data.to = stripSensitiveQueryParams(breadcrumb.data.to)
          }
        }
      }
      return event
    })

    Sentry.setUser({
      id: String(user.id),
      email: user.email,
      // Saved Sentry searches and alert rules filter on these keys, so they keep the names from before teams.
      organization: user.team.name,
      organizationId: String(user.teamId),
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
    })

    this.loggerService.info(`Sentry initialized with environment ${user.environment} and user ${user.email} (${user.id}).`)
  }
}
